import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { User, Settings, LogOut, Shield, BarChart, History, Bookmark, ChevronDown, Building, Calculator, Zap, Menu, ClipboardList, FileText, Heart, Brain, Users as UsersIcon } from 'lucide-react';
import { LanguageSwitcher } from '@/components/language/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { useSurveyTemplates } from '@/hooks/useSurveyTemplates';

interface TopNavbarProps {
  showLanguageSwitcher?: boolean;
}

export const TopNavbar: React.FC<TopNavbarProps> = ({ showLanguageSwitcher = true }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang?: string }>();
  const prefix = `/${lang || 'en'}`;
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const { t } = useTranslation('common');
  const { data: surveyTemplates = [], isLoading: templatesLoading } = useSurveyTemplates();

  const categoryIconMap: Record<string, any> = {
    engagement: Heart,
    onboarding: UsersIcon,
    exit: FileText,
    pulse: ClipboardList,
    wellbeing: Heart,
    culture: UsersIcon,
    performance: BarChart,
    feedback: FileText,
  };

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) return;

      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin, avatar_url')
          .eq('user_id', user.id)
          .single();

        setIsAdmin(profile?.is_admin || false);
        setAvatarUrl(profile?.avatar_url || null);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
        setAvatarUrl(null);
      }
    };

    checkAdminStatus();
  }, [user]);

  return (
    <div className="relative z-50 h-16 bg-white/80 supports-[backdrop-filter]:bg-white/60 backdrop-blur border-b border-border flex items-center justify-between px-6 text-foreground">
      {/* Left side - Logo/Brand and Navigation */}
      <div className="flex items-center gap-8">
        {/* Mobile menu */}
        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72">
              <SheetHeader>
                <SheetTitle>{t('nav.menu') || 'Menu'}</SheetTitle>
              </SheetHeader>
              <nav className="mt-4 grid gap-2">
                <SheetClose asChild>
                  <Link to={prefix} className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                    {t('nav.tools')}
                  </Link>
                </SheetClose>
                {/* Added Survey Templates link to mobile menu */}
                <SheetClose asChild>
                  <Link to={`${prefix}/survey-templates`} className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                    {t('nav.surveyTemplates') || 'Survey Templates'}
                  </Link>
                </SheetClose>

                {/* <div className="pt-2">
                  <p className="text-xs text-muted-foreground mb-1">{t('nav.business')}</p>
                  <div className="grid gap-1">
                    <SheetClose asChild>
                      <button onClick={() => navigate(`${prefix}/integrations`)} className="text-left text-sm text-foreground hover:text-primary transition-colors flex items-center gap-2">
                        <Zap className="h-4 w-4" />
                        {t('nav.integrations')}
                      </button>
                    </SheetClose>
                    <SheetClose asChild>
                      <button onClick={() => navigate(`${prefix}/enterprise`)} className="text-left text-sm text-foreground hover:text-primary transition-colors flex items-center gap-2">
                        <Building className="h-4 w-4" />
                        {t('nav.enterprise')}
                      </button>
                    </SheetClose>
                    <SheetClose asChild>
                      <button onClick={() => navigate(`${prefix}/roi-calculator`)} className="text-left text-sm text-foreground hover:text-primary transition-colors flex items-center gap-2">
                        <Calculator className="h-4 w-4" />
                        {t('nav.roi')}
                      </button>
                    </SheetClose>
                  </div>
                </div> */}

                <div className="pt-4">
                  <p className="text-xs text-muted-foreground mb-1">{t('nav.language') || 'Language'}</p>
                  {showLanguageSwitcher && <LanguageSwitcher />}
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>

        <Link to={prefix} className="flex items-center gap-2">
          <img 
            src="/lovable-uploads/JiGSO_analytics.png" 
            alt="Jigso logo" 
            className="w-8 h-8 "
          />
          <span className="font-semibold text-lg">JiGSO EX Hub</span>
        </Link>
        
        {/* Navigation Links */}
        <nav className="hidden md:flex items-center gap-6">
          <Link to={prefix} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            {t('nav.tools')}
          </Link>
          {/* Survey Templates with hover preview */}
          <HoverCard openDelay={100} closeDelay={100}>
            <HoverCardTrigger asChild>
              <Link to={`${prefix}/survey-templates`} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t('nav.surveyTemplates') || 'Survey Templates'}
              </Link>
            </HoverCardTrigger>
            <HoverCardContent align="start" className="w-[420px] z-[60]">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">{t('nav.surveyTemplates') || 'Survey Templates'}</div>
                <Link to={`${prefix}/survey-templates`} className="text-xs text-primary hover:underline">
                  View all
                </Link>
              </div>
              <div className="space-y-2">
                {templatesLoading && (
                  <div className="text-sm text-muted-foreground">Loading...</div>
                )}
                {!templatesLoading && surveyTemplates.length === 0 && (
                  <div className="text-sm text-muted-foreground">No templates available</div>
                )}
                {!templatesLoading && surveyTemplates.slice(0, 4).map((tmpl) => {
                  const Icon = categoryIconMap[(tmpl as any).category?.toLowerCase?.() || ''] || ClipboardList;
                  return (
                    <button
                      key={tmpl.id}
                      onClick={() => navigate(`${prefix}/survey-templates/${(tmpl as any).slug}`)}
                      className="w-full flex items-start gap-3 p-2 rounded-md hover:bg-muted/50 transition text-left"
                    >
                      <div className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{(tmpl as any).name}</div>
                        {(tmpl as any).description && (
                          <div className="text-xs text-muted-foreground line-clamp-2">{(tmpl as any).description}</div>
                        )}
                      </div>
                    </button>
                  );
                })}
                {!templatesLoading && surveyTemplates.length > 4 && (
                  <Link
                    to={`${prefix}/survey-templates`}
                    className="block text-xs text-primary hover:underline pt-1"
                  >
                    View all {surveyTemplates.length} templates
                  </Link>
                )}
              </div>
            </HoverCardContent>
          </HoverCard>
          
          {/* Business Solutions Dropdown */}
          {/* <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors p-0 h-auto font-normal flex items-center gap-1">
                {t('nav.business')}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuItem onClick={() => navigate(`${prefix}/integrations`)}>
                <Zap className="mr-2 h-4 w-4" />
                {t('nav.integrations')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`${prefix}/enterprise`)}>
                <Building className="mr-2 h-4 w-4" />
                {t('nav.enterprise')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`${prefix}/roi-calculator`)}>
                <Calculator className="mr-2 h-4 w-4" />
                {t('nav.roi')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu> */}
        </nav>
      </div>

      {/* Right side - Language + User menu or Sign In */}
      <div className="flex items-center gap-3">
        {showLanguageSwitcher && (
          <div className="hidden sm:block">
            <LanguageSwitcher />
          </div>
        )}
        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 p-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={avatarUrl || ''} alt="Profile" />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {user.email?.[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden md:inline text-sm text-foreground/80">{user.email}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate(`${prefix}/profile`)}>
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate(`${prefix}/my-generations`)}>
                <History className="mr-2 h-4 w-4" />
                My Content
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`${prefix}/bookmarks`)}>
                <Bookmark className="mr-2 h-4 w-4" />
                Bookmarked Tools
              </DropdownMenuItem>
              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(`${prefix}/analytics`)}>
                    <BarChart className="mr-2 h-4 w-4" />
                    Analytics
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(`${prefix}/admin`)}>
                    <Shield className="mr-2 h-4 w-4" />
                    Admin Dashboard
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={signOut}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <>
            {/* Desktop: Show both Sign In and Sign Up */}
            <div className="hidden md:flex items-center gap-3">
              <Link to={`${prefix}/auth?tab=signin`}>
                <Button variant="outline" className="hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-all duration-200 font-medium">
                  Sign In
                </Button>
              </Link>
              <Link to={`${prefix}/auth?tab=signup`}>
                <Button variant="default">
                  Sign Up
                </Button>
              </Link>
            </div>
            
            {/* Mobile: Show only Sign In */}
            <div className="md:hidden">
              <Link to={`${prefix}/auth?tab=signin`}>
                <Button variant="outline" className="hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-all duration-200 font-medium">
                  Sign In
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};